import { useMemo, useState, useCallback } from 'react';
import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { usePreferencesContext } from 'contexts/preferences';
import { getSmartChatBetaStatus, setSmartChatOptIn, setSmartChatOptOut, SmartChatBetaStatus } from 'utils/cookies';
import PillBadge from 'components/atomic/organisms/pill-badge';
import styles from '../components/dropdown/style.module.scss';
import { useIsDesktop } from 'hooks/use-size-class';
import { trackSmartchatOptIn, trackSmartchatOptOut } from 'utils/smartchat-tracking';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

type MenuItem = {
  id: string;
  label: string;
  content?: React.ReactNode;
  link?: string;
  isLink?: boolean;
  newTab?: boolean;
  mobileOnly?: boolean;
  signOut?: boolean;
  onClick?: () => void;
};

type UseSmartChatMenuItemParams = {
  baseMenuItems: MenuItem[];
  isFeatureEnabled: boolean;
};

/**
 * Custom hook to manage SmartChat Beta menu item.
 * 
 * Handles the logic for:
 * - Displaying dynamic menu label based on opt-in status
 * - Rendering beta badge
 * - Toggling opt-in/opt-out state
 * - Navigation to search page when enabling
 * 
 * @param params Configuration object
 * @returns Object containing enhanced menuItems with SmartChat Beta item
 */
export function useSmartChatMenuItem({
  baseMenuItems,
  isFeatureEnabled,
}: UseSmartChatMenuItemParams): { menuItems: MenuItem[] } {
  const router = useRouter();
  const { t } = useTranslation(Namespaces.COMMON);
  const { setIsAiSearch } = usePreferencesContext();
  const [smartChatBetaStatus, setSmartChatBetaStatus] = useState(getSmartChatBetaStatus());
  const isDesktop = useIsDesktop();

  const handleSmartChatBetaToggle = useCallback(() => {
    const currentStatus = getSmartChatBetaStatus();
    if (currentStatus === SmartChatBetaStatus.OPTED_IN) {
      // Opt out
      setSmartChatOptOut();
      setIsAiSearch(false);
      setSmartChatBetaStatus(SmartChatBetaStatus.OPTED_OUT);
      
      // Track analytics
      trackSmartchatOptOut();
    } else {
      // Opt in (for both undecided and opted_out)
      setSmartChatOptIn();
      setIsAiSearch(true);
      setSmartChatBetaStatus(SmartChatBetaStatus.OPTED_IN);
      
      // Track analytics
      trackSmartchatOptIn();
      
      // Navigate to search page
      router.push('/search');
    }
  }, [setIsAiSearch, router]);

  const smartChatMenuLabel = useMemo(() => {
    switch (smartChatBetaStatus) {
      case SmartChatBetaStatus.OPTED_IN:
        return t(LOCALE_COMMON_PATHS.SMART_CHAT_MENU_DISABLE, { defaultValue: 'Disable SmartChat' });
      case SmartChatBetaStatus.OPTED_OUT:
        return t(LOCALE_COMMON_PATHS.SMART_CHAT_MENU_ENABLE, { defaultValue: 'Enable SmartChat' });
      case SmartChatBetaStatus.UNDECIDED:
      default:
        return t(LOCALE_COMMON_PATHS.SMART_CHAT_MENU_TRY, { defaultValue: 'Try SmartChat' });
    }
  }, [smartChatBetaStatus, t]);

  const smartChatMenuContent = useMemo(() => {
    return (
      <PillBadge 
        backgroundColor="var(--eXp-Secondary-Brand-Royal, #1B489B)"
        textColor="#FFFFFF"
        size="small"
        className={styles['menu-beta-badge']}
      >
        BETA
      </PillBadge>
    );
  }, []);

  const menuItems = useMemo(() => {
    // Only show SmartChat menu item on desktop (not mobile or tablet)
    if (!isFeatureEnabled || !isDesktop) {
      return baseMenuItems;
    }

    const smartChatMenuItem: MenuItem = {
      id: 'smartchat-beta',
      label: smartChatMenuLabel,
      content: smartChatMenuContent,
      isLink: false,
      onClick: handleSmartChatBetaToggle,
    };

    // Add SmartChat Beta item at the end of the menu
    return [...baseMenuItems, smartChatMenuItem];
  }, [isFeatureEnabled, isDesktop, smartChatMenuLabel, smartChatMenuContent, baseMenuItems, handleSmartChatBetaToggle]);

  return {
    menuItems,
  };
}
